import type { components } from "./types.ts";

/**
 * El unico sitio donde se habla HTTP (SPEC 15.3).
 *
 * Aqui se resuelven, una sola vez, las cuatro cosas transversales:
 *
 * 1. La cabecera `X-Requested-With: TradingJournal` en TODA mutacion. Junto con
 *    la cookie SameSite=Strict es la defensa CSRF completa (SPEC 5), y sin ella
 *    el servidor responde 403.
 * 2. `credentials: "same-origin"`: la sesion es una cookie HttpOnly, nunca un
 *    token en localStorage (SPEC 19.18).
 * 3. Cualquier respuesta no-2xx se convierte en `ApiError` a partir del
 *    ProblemDetails, con su `code`, sus `errors` por campo y su `traceId`.
 * 4. Un 401 en CUALQUIER llamada dispara un evento global. `RequireSession` lo
 *    escucha y lleva al inicio de sesion. Ninguna pantalla trata el 401 por su
 *    cuenta (SPEC 19.14).
 */

export type Schemas = components["schemas"];

export type ProblemDetails = {
  status?: number;
  title?: string;
  detail?: string;
  code?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
};

/** El evento global del 401. Lo escucha RequireSession y nadie mas. */
export const SESSION_EXPIRED = "tj:session-expired";

/**
 * Cuando se hablo por ultima vez con el servidor, para el aviso de inactividad
 * (ADR 0005, punto 2).
 *
 * Vive AQUI porque aqui pasan todas las peticiones, y porque es exactamente lo
 * que el servidor mide: la inactividad se cuenta contra la ultima PETICION, no
 * contra el ultimo movimiento del raton. Mover el raton en una pestana abierta
 * no manda nada y no mantiene viva ninguna sesion; creer lo contrario es como se
 * pierde medio formulario escrito.
 */
let lastContactAt = Date.now();

export function lastServerContactAt(): number {
  return lastContactAt;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: Record<string, string[]>;
  readonly traceId: string | undefined;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `Error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = problem.code ?? "Unknown";
    this.errors = problem.errors ?? {};
    this.traceId = problem.traceId;
  }

  /** El mensaje de un campo concreto, para pintarlo junto a su input (SPEC 5). */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0];
  }

  /**
   * Los errores que NO casan con ningun campo del formulario van a un resumen
   * arriba (SPEC 15.3): si no, el usuario ve un formulario sin marcas y un
   * guardado que no ocurre.
   */
  unmatchedErrors(fields: readonly string[]): string[] {
    return Object.entries(this.errors)
      .filter(([field]) => !fields.includes(field))
      .flatMap(([, messages]) => messages);
  }

  get isConcurrencyConflict(): boolean {
    return this.code === "Concurrency.Conflict";
  }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

async function send<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};

  if (method !== "GET") {
    headers["X-Requested-With"] = "TradingJournal";
  }

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  return await unwrap<T>(response);
}

/**
 * El unico sitio donde se decide que hacer con una respuesta: el 401 global, el
 * ProblemDetails convertido en `ApiError` y el 204 sin cuerpo. Lo comparten las
 * peticiones JSON y la subida multipart, porque esas tres reglas no cambian por
 * el tipo de cuerpo que se envio.
 */
async function unwrap<T>(response: Response): Promise<T> {
  /* Se sella aunque la respuesta sea un error: el reloj de inactividad del
     servidor se reinicia con la peticion, no con el exito. */
  lastContactAt = Date.now();

  if (response.status === 401) {
    window.dispatchEvent(new Event(SESSION_EXPIRED));
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * La subida de un archivo (restaurar la base, ADR 0005 punto 3).
 *
 * Va aparte de `send` por una razon concreta: aqui NO se pone `Content-Type`.
 * El navegador tiene que ponerlo el mismo para anadir el `boundary` del
 * multipart, y fijarlo a mano produce un cuerpo que el servidor no sabe partir.
 * La cabecera `X-Requested-With` sigue haciendo falta igual: es una mutacion.
 */
async function sendForm<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "X-Requested-With": "TradingJournal" },
    credentials: "same-origin",
    body: form,
  });

  return await unwrap<T>(response);
}

/**
 * La subida de UNA captura, con progreso (SPEC 14 y 15.6).
 *
 * Va con `XMLHttpRequest` y no con `fetch`, y es la unica razon por la que esta
 * API vieja sigue aqui: `fetch` no informa de cuanto lleva SUBIDO. Hay un
 * `ReadableStream` en el cuerpo de la peticion que lo permitiria, pero exige
 * HTTP/2, `duplex: "half"` y no lo implementa Safari, que es justo el navegador
 * del telefono desde el que se suben las capturas. Sin progreso, una foto de 6 MB
 * en una conexion de campo es una barra que no se mueve durante cuarenta
 * segundos, y eso se lee como "se colgo", no como "esta subiendo".
 *
 * Las cuatro reglas transversales se cumplen igual, a mano y en el mismo orden
 * que en `unwrap`: `X-Requested-With` porque es una mutacion, la cookie de
 * sesion que el navegador manda sola por ser mismo origen, el ProblemDetails
 * convertido en `ApiError`, y el 401 disparando el evento global.
 */
export type UploadProgress = { sent: number; total: number };

function upload<T>(
  path: string,
  form: FormData,
  onProgress: (progress: UploadProgress) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open("POST", path);
    request.setRequestHeader("X-Requested-With", "TradingJournal");

    /* `withCredentials` no se toca: es lo que gobierna las peticiones a OTRO
       origen, y aqui no hay ninguno (SPEC 5 prohibe CORS). En el mismo origen
       la cookie de sesion viaja siempre. */

    request.upload.addEventListener("progress", (event) => {
      /* Sin `lengthComputable` no hay total, y un porcentaje inventado es peor
         que ninguno: la pantalla ensena la barra indeterminada. */
      if (event.lengthComputable) {
        onProgress({ sent: event.loaded, total: event.total });
      }
    });

    request.addEventListener("load", () => {
      lastContactAt = Date.now();

      if (request.status === 401) {
        window.dispatchEvent(new Event(SESSION_EXPIRED));
      }

      if (request.status >= 200 && request.status < 300) {
        resolve(parseBody<T>(request.responseText));

        return;
      }

      reject(new ApiError(request.status, parseProblem(request.responseText, request.status)));
    });

    /* Se cae la red a mitad de la subida. No hay respuesta, asi que no hay
       estado: se avisa con el mismo texto que `readProblem` para lo mismo. */
    request.addEventListener("error", () => {
      lastContactAt = Date.now();
      reject(new ApiError(0, { detail: "No se pudo contactar con el servidor." }));
    });

    request.addEventListener("abort", () => {
      reject(new ApiError(0, { detail: "La subida se cancelo." }));
    });

    request.send(form);
  });
}

function parseBody<T>(text: string): T {
  if (text === "") return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as T;
  }
}

function parseProblem(text: string, status: number): ProblemDetails {
  try {
    return JSON.parse(text) as ProblemDetails;
  } catch {
    return { status, detail: "No se pudo contactar con el servidor." };
  }
}

/** Un archivo que el servidor manda para guardar, con el nombre que el eligio. */
export type FileDownload = { blob: Blob; fileName: string };

const FALLBACK_DOWNLOAD_NAME = "descarga";

/**
 * Una descarga que el servidor PUEDE rechazar.
 *
 * Un `<a href>` a secas seria mas barato: el navegador escribe a disco solo y
 * no pasa nada por memoria. Pero el respaldo falla de una forma muy concreta y
 * muy probable —que no haya `pg_dump` instalado, un 503 en `problem+json`— y
 * entonces el enlace desmonta la aplicacion para dejar un JSON crudo en la
 * pantalla. Con `download` puesto es peor todavia: guarda el cuerpo del error
 * con el nombre del respaldo, o sea un archivo roto con pinta de respaldo bueno.
 *
 * Asi que se pide con `fetch`, se comprueba el estado y solo entonces se
 * guarda. El precio es que el volcado pasa entero por la memoria del navegador;
 * se paga a gusto, porque lo que se compra es que un fallo se lea en la
 * pantalla, con el nombre del binario que falta y donde se busco.
 */
async function download(path: string): Promise<FileDownload> {
  const response = await fetch(path, { credentials: "same-origin" });

  lastContactAt = Date.now();

  if (response.status === 401) {
    window.dispatchEvent(new Event(SESSION_EXPIRED));
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  return { blob: await response.blob(), fileName: fileNameOf(response) };
}

/**
 * El nombre del archivo lo decide el SERVIDOR y viaja en `Content-Disposition`.
 * Aqui solo se lee: el nombre lleva la base y la marca de tiempo del volcado, y
 * reinventarlo en el cliente daria dos respaldos distintos con el mismo nombre.
 */
function fileNameOf(response: Response): string {
  const header = response.headers.get("Content-Disposition") ?? "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  const raw = encoded?.[1] ?? plain?.[1];

  if (raw === undefined) return FALLBACK_DOWNLOAD_NAME;

  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    // Un 502 de nginx o una desconexion a medias no traen ProblemDetails. Aun
    // asi hay que decir algo util y no un "undefined".
    return { status: response.status, detail: "No se pudo contactar con el servidor." };
  }
}

export const api = {
  get: <T>(path: string) => send<T>("GET", path),
  post: <T>(path: string, body?: unknown) => send<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => send<T>("PUT", path, body),
  del: <T>(path: string) => send<T>("DELETE", path),
  postForm: <T>(path: string, form: FormData) => sendForm<T>(path, form),
  upload: <T>(path: string, form: FormData, onProgress: (progress: UploadProgress) => void) =>
    upload<T>(path, form, onProgress),
  download: (path: string) => download(path),
};

/** Construye una query string saltandose lo que no venga. */
export function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }

  const text = search.toString();

  return text === "" ? "" : `?${text}`;
}
